import { z } from 'zod';
import { contrastWithWhite, MIN_PRIMARY_CONTRAST } from '../color';
import { MODULES, PERMISSION_LEVELS, TENANT_ROLES } from '../roles';
import { LocaleSchema, TenantSettingsSchema } from '../settings';
import { HEX_COLOR } from './tenant';

const optText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => v || null)
    .nullable();

/** ScrSettings « Établissement ». */
export const EstablishmentSchema = z.object({
  name: z.string().trim().min(2).max(160),
  shortName: z.string().trim().min(1).max(12),
  legalName: optText(200),
  agrementNo: optText(40),
  address: optText(200),
  phone: optText(40),
  email: z
    .union([z.literal(''), z.email()])
    .transform((v) => v || null)
    .nullable(),
  locales: z
    .object({ default: LocaleSchema, enabled: z.array(LocaleSchema).min(1) })
    .refine((l) => l.enabled.includes(l.default), {
      path: ['default'],
      message: 'settings.errors.defaultLocale',
    }),
});
export type EstablishmentInput = z.input<typeof EstablishmentSchema>;

export const PrimaryColorSchema = z
  .string()
  .regex(HEX_COLOR, 'settings.errors.color')
  .refine((c) => contrastWithWhite(c) >= MIN_PRIMARY_CONTRAST, 'settings.errors.contrast');
export const BrandingSchema = z.object({ primaryColor: PrimaryColorSchema });

/**
 * Sections of `tenant_settings` editable from the settings screen. Each is replaced as a whole; the server
 * merges it into the stored settings and validates the result (optimistic locking on `version`).
 */
const SECTION_SCHEMAS = {
  grading: TenantSettingsSchema.shape.grading,
  attendance: TenantSettingsSchema.shape.attendance,
  timetable: TenantSettingsSchema.shape.timetable,
  features: TenantSettingsSchema.shape.features,
  documents: TenantSettingsSchema.shape.documents,
  identifiers: TenantSettingsSchema.shape.identifiers,
};
type SectionKey = keyof typeof SECTION_SCHEMAS;
export type SettingsSections = { [K in SectionKey]?: z.output<(typeof SECTION_SCHEMAS)[K]> };

/**
 * Only the sections present in the request. Not `.partial()`: in Zod 4 a missing key of an optional
 * schema with defaults is filled with those defaults, which silently reset the other sections on save.
 */
const Sections = z.record(z.string(), z.unknown()).transform((raw, ctx): SettingsSections => {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (!(key in SECTION_SCHEMAS)) {
      ctx.addIssue({ code: 'custom', path: [key], message: 'settings.errors.section' });
      continue;
    }
    const r = SECTION_SCHEMAS[key as SectionKey].safeParse(value);
    if (r.success) out[key] = r.data;
    else
      for (const issue of r.error.issues)
        ctx.addIssue({ code: 'custom', path: [key, ...issue.path], message: issue.message });
  }
  return out;
});

const mins = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));

export const SettingsSectionsSchema = z.object({
  version: z.number().int(),
  sections: Sections.superRefine((s, ctx) => {
    const a = s.attendance;
    if (a && !(a.watchPct <= a.alertPct && (a.eliminationPct === null || a.alertPct <= a.eliminationPct)))
      ctx.addIssue({
        code: 'custom',
        path: ['attendance', 'alertPct'],
        message: 'settings.errors.thresholds',
      });
    const tt = s.timetable;
    if (tt) {
      if (!tt.weekdays.length)
        ctx.addIssue({
          code: 'custom',
          path: ['timetable', 'weekdays'],
          message: 'settings.errors.weekdays',
        });
      const slots = [...tt.slots].sort((x, y) => x.start.localeCompare(y.start));
      slots.forEach((sl, i) => {
        const prev = slots[i - 1];
        if (mins(sl.end) <= mins(sl.start) || (prev && mins(sl.start) < mins(prev.end)))
          ctx.addIssue({ code: 'custom', path: ['timetable', 'slots', i], message: 'settings.errors.slots' });
      });
    }
    const g = s.grading;
    if (g && g.eliminatoryUeGrade !== null && g.eliminatoryUeGrade >= g.passMark)
      ctx.addIssue({
        code: 'custom',
        path: ['grading', 'eliminatoryUeGrade'],
        message: 'settings.errors.eliminatory',
      });
  }),
});
export type SettingsSectionsInput = z.input<typeof SettingsSectionsSchema>;

export const SettingsViewSchema = z.object({
  establishment: z.object({
    name: z.string(),
    shortName: z.string(),
    legalName: z.string().nullable(),
    agrementNo: z.string().nullable(),
    address: z.string().nullable(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
  }),
  branding: z.object({ primaryColor: z.string(), logoUrl: z.string().nullable() }),
  settings: TenantSettingsSchema,
  version: z.number().int(),
});
export type SettingsView = z.infer<typeof SettingsViewSchema>;

// ── Permissions ─────────────────────────────────────────────────────────────
const Level = z.enum(PERMISSION_LEVELS);
export const PermissionMatrixSchema = z.record(z.enum(TENANT_ROLES), z.record(z.enum(MODULES), Level));
export const PermissionsViewSchema = z.object({
  matrix: PermissionMatrixSchema,
  /** Active accounts per role (column header « n comptes »). */
  accounts: z.record(z.enum(TENANT_ROLES), z.number().int()),
});
export type PermissionsView = z.infer<typeof PermissionsViewSchema>;
export const PermissionChangeSchema = z.object({
  changes: z
    .array(z.object({ role: z.enum(TENANT_ROLES), module: z.enum(MODULES), level: Level }))
    .min(1)
    .max(200),
});
export type PermissionChange = z.infer<typeof PermissionChangeSchema>;

// ── Academic years ──────────────────────────────────────────────────────────
const IsoDate = z.iso.date();
export const YearDatesSchema = z
  .object({ startsOn: IsoDate, s1EndsOn: IsoDate, s2EndsOn: IsoDate, endsOn: IsoDate })
  .refine((y) => y.startsOn < y.s1EndsOn && y.s1EndsOn < y.s2EndsOn && y.s2EndsOn <= y.endsOn, {
    path: ['s1EndsOn'],
    message: 'settings.errors.yearDates',
  });
export const NewYearSchema = YearDatesSchema.and(
  z.object({
    label: z.string().regex(/^\d{4}-\d{4}$/, 'settings.errors.yearLabel'),
    copyGroups: z.boolean().default(true),
    copyFees: z.boolean().default(true),
    copySlots: z.boolean().default(false),
  }),
);
export type NewYearInput = z.input<typeof NewYearSchema>;
export const YearDetailSchema = z.object({
  id: z.uuid(),
  label: z.string(),
  startsOn: z.string(),
  s1EndsOn: z.string().nullable(),
  s2EndsOn: z.string().nullable(),
  endsOn: z.string(),
  status: z.enum(['draft', 'current', 'closed', 'archived']),
  groups: z.number().int(),
  enrollments: z.number().int(),
});
export type YearDetail = z.infer<typeof YearDetailSchema>;
export const NewYearResultSchema = z.object({
  id: z.uuid(),
  groups: z.number().int(),
  fees: z.number().int(),
  slots: z.number().int(),
});

// ── Rooms (Structure section) ───────────────────────────────────────────────
export const RoomInputSchema = z.object({
  code: z.string().trim().min(1).max(20),
  name: z.string().trim().min(1).max(80),
  kind: z.enum(['amphi', 'salle', 'labo']),
  capacity: z.number().int().min(1).max(2000),
});
export type RoomInput = z.infer<typeof RoomInputSchema>;

// ── Platform console ────────────────────────────────────────────────────────
export const TenantCreateSchema = z.object({
  slug: z
    .string()
    .trim()
    .toLowerCase()
    .min(1, 'platform.errors.required')
    .regex(/^[a-z0-9](?:[a-z0-9-]{1,30}[a-z0-9])$/, 'platform.errors.slug')
    .refine((s) => !['admin', 'www', 'api', 'app'].includes(s), 'platform.errors.slugReserved'),
  name: z.string().trim().min(2, 'platform.errors.required').max(160),
  shortName: z.string().trim().min(1, 'platform.errors.required').max(12, 'platform.errors.shortName'),
  primaryColor: PrimaryColorSchema,
  admin: z.object({
    firstName: z.string().trim().min(1, 'platform.errors.required').max(80),
    lastName: z.string().trim().min(1, 'platform.errors.required').max(80),
    email: z.email('platform.errors.email'),
  }),
  /** First academic year, created as current. */
  year: YearDatesSchema.and(
    z.object({ label: z.string().regex(/^\d{4}-\d{4}$/, 'settings.errors.yearLabel') }),
  ),
});
export type TenantCreateInput = z.input<typeof TenantCreateSchema>;
export const TenantStatusSchema = z.object({ status: z.enum(['active', 'suspended']) });
